import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Fingerprint, Lock, Eye, EyeOff } from 'lucide-react';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import EmbeddedBrowserBanner from '@/components/EmbeddedBrowserBanner';
import AppleSignInButton from '@/components/AppleSignInButton';
import { detectEmbeddedBrowser } from '@/utils/embeddedBrowser';

const EMBEDDED = detectEmbeddedBrowser();

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Surface a clear "your session expired" banner when the silent-refresh
  // flow gives up and the global interceptor bounces us back to /login.
  const [sessionExpired, setSessionExpired] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  React.useEffect(() => {
    try {
      if (sessionStorage.getItem('healthloop_session_expired') === '1') {
        setSessionExpired(true);
        sessionStorage.removeItem('healthloop_session_expired');
      }
    } catch (_) { /* storage disabled — ignore */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpired(false);
    setLoading(true);

    // Trim the email defensively — mobile keyboards (notably Samsung
    // Android) sometimes append a trailing space when auto-completing
    // an email address, which makes the backend lookup miss. The
    // password is NOT trimmed — legitimate passwords can contain
    // intentional whitespace.
    const result = await login(email.trim(), password);
    setLoading(false);

    if (result.success) {
      // Admins land on the dedicated admin overview; everyone else gets
      // the regular user dashboard. The result payload from `login()` is
      // the user document (includes `role`).
      navigate(result.user?.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-6">
      <Card className="w-full max-w-md shadow-xl border-0 rounded-2xl" data-testid="login-card">
        <CardContent className="pt-10 pb-10 px-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <div className="mb-4">
              <HealthLoopLogo size="md" showText={false} />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight mb-2">
              HealthLoop
            </h1>
          </div>

          {/* Primary Value Proposition (reordered - main message first) */}
          <p className="text-center text-slate-700 text-base font-medium mb-1.5 leading-relaxed">
            {t('auth.tagline')}
          </p>

          {/* Secondary Helper Text (reassurance) */}
          <p className="text-center text-slate-500 text-xs mb-8">
            {t('auth.takes30s')}
          </p>

          {/* Embedded-WebView guard — Google blocks OAuth from in-app
              browsers (Instagram / FB / LinkedIn / etc) via the
              "Use Secure Browsers" policy. Render a calm banner with a
              one-tap escape to the real browser, and disable the Google
              button so users never hit Error 403. */}
          {EMBEDDED.isEmbedded && <EmbeddedBrowserBanner label={EMBEDDED.label} />}

          {/* Continue with Google — primary social CTA, sits above the
              email/password form per mobile-onboarding best practice.
              REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH. The redirect MUST be
              derived from window.location.origin at click time. */}
          <button
            type="button"
            disabled={EMBEDDED.isEmbedded}
            onClick={() => {
              if (EMBEDDED.isEmbedded) return;  // hard-guard; banner explains why
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className={`w-full h-12 mb-4 flex items-center justify-center gap-3 rounded-xl border bg-white transition-all duration-200 text-slate-700 font-medium shadow-sm ${
              EMBEDDED.isEmbedded
                ? 'border-slate-200 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:bg-slate-50 active:scale-[0.97] hover:shadow'
            }`}
            data-testid="continue-with-google-button"
            aria-label={t('auth.continueWithGoogle')}
            aria-disabled={EMBEDDED.isEmbedded}
          >
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>{t('auth.continueWithGoogle')}</span>
          </button>

          {/* Continue with Apple — scaffolded behind ENABLE_APPLE_SIGNIN
              feature flag. Renders nothing until the operator completes
              Apple Developer enrolment and flips the env var to true. */}
          <AppleSignInButton testIdPrefix="login-" />

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4" aria-hidden="true">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wide text-slate-400">{t('auth.orDivider')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {sessionExpired && !error && (
              <div
                className="bg-amber-50 text-amber-800 p-3 rounded-xl text-sm border border-amber-200"
                data-testid="login-session-expired-banner"
                role="status"
              >
                {t('auth.sessionExpired')}
              </div>
            )}
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm border border-red-100" data-testid="login-error">
                {error}
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/10 transition-all placeholder:text-slate-400"
                data-testid="email-input"
              />
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/10 transition-all placeholder:text-slate-400 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-slate-400 hover:text-slate-600 rounded-md transition-colors"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link - Tighter spacing */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-600 hover:text-emerald-600 transition-colors"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            {/* Continue Button - Shortened text, deeper green, scale 0.97 */}
            <div className="pt-1">
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/30 transition-all duration-200 active:scale-[0.97]"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('auth.checking')}
                  </span>
                ) : (
                  t('auth.continue')
                )}
              </Button>
            </div>
          </form>

          {/* Security Message - Subtle with lock icon */}
          <div className="mt-6 text-center">
            <div className="flex items-center justify-center gap-1.5 text-slate-500/75">
              <Lock className="h-3 w-3" />
              <p className="text-xs">
                {t('auth.secureMessage')}
              </p>
            </div>
          </div>

          {/* Create Account Link - Enhanced weight and interaction */}
          <div className="mt-5 text-center">
            <p className="text-sm text-slate-600">
              {t('auth.noAccount')}{' '}
              <Link
                to="/register"
                className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors underline-offset-2 hover:underline"
              >
                {t('auth.createOne')}
              </Link>
            </p>
          </div>

          {/* Biometric Coming Soon - Muted with fingerprint icon */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-center gap-2 text-slate-400/70">
              <Fingerprint className="h-3.5 w-3.5" />
              <p className="text-xs">
                {t('auth.biometricSoon')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
